import {
  ActionIcon,
  AppShell,
  Button,
  Checkbox,
  Combobox,
  HoverCard,
  Indicator,
  Input,
  InputWrapper,
  Loader,
  Modal,
  NativeSelect,
  NumberInput,
  Pill,
  Popover,
  Switch,
  Textarea,
  Tabs,
  Text,
  Title,
  Tooltip,
  TypographyStylesProvider,
  createTheme,
  rem,
} from '@mantine/core';

import actionIconClasses from '@/styles/globals/ActionIcon.module.css';
import tabsClasses from '@/styles/globals/Tabs.module.css';
import typographyClasses from '@/styles/globals/Typography.module.css';
import textClasses from '@/styles/globals/Text.module.css';
import componentClasses from './components.module.css';

// Keys like sm/mm/lm are max-width thresholds (one unit below s/m/lg) for media query ranges
const breakpoints = {
  xs: '36em',
  s: '48em',
  sm: '47.9375em',
  m: '62em',
  mm: '61.9375em',
  lg: '80em',
  lm: '79.9375em',
  xl: '90em',
};

const fontFamily = 'Inter, sans-serif';

const headings = {
  fontFamily,
  sizes: {
    h1: {
      fontSize: rem(24),
      fontWeight: '700',
      lineHeight: rem(32),
    },
    h2: {
      fontSize: rem(20),
      fontWeight: '400',
      lineHeight: '1.4',
    },
    h3: {
      fontSize: rem(18),
      fontWeight: '600',
      lineHeight: '1.4',
    },
    h4: {
      fontSize: rem(16),
      fontWeight: '600',
      lineHeight: '1.5',
    },
    h5: {
      fontSize: rem(14),
      fontWeight: '600',
      lineHeight: '1.5',
    },
  },
};

export const mantineTheme = createTheme({
  breakpoints,
  fontFamily,
  headings,
  components: {
    Button: Button.extend({
      vars: (_theme, props) => {
        const sizeMap: Record<string, { height: string; px: string; fz: string }> = {
          lg: { height: '48px', px: '24px', fz: '16px' },
          md: { height: '40px', px: '16px', fz: '16px' },
          sm: { height: '32px', px: '12px', fz: '14px' },
          xs: { height: '24px', px: '12px', fz: '14px' },
        };
        const s = sizeMap[props.size as string] ?? sizeMap.md;
        return {
          root: {
            '--button-radius': 'var(--ds-border-radius-xs)',
            '--button-bg': 'var(--ds-accent-blue-500)',
            '--button-color': 'var(--ds-always-white)',
            '--button-height': s.height,
            '--button-padding-x': s.px,
            '--button-fz': s.fz,
          },
        };
      },
    }),
    Loader: Loader.extend({
      vars: (_theme, props) => ({
        root: {
          '--loader-color': props.color ?? 'var(--ds-platform-l700)',
        },
      }),
    }),
    Tooltip: Tooltip.extend({
      styles: {
        tooltip: {
          borderRadius: '2px',
        },
      },
    }),
    Text: Text.extend({
      classNames: textClasses,
    }),
    AppShell: AppShell.extend({
      styles: {
        navbar: {
          backgroundColor: 'var(--ds-background)',
          borderRightColor: 'light-dark(var(--ds-border-line), transparent)',
        },
        header: {
          backgroundColor: 'var(--ds-background)',
          borderBottomColor: 'var(--ds-border-line)',
        },
      },
    }),
    Input: Input.extend({
      defaultProps: { variant: 'default' },
      classNames: { input: componentClasses.inputField },
      styles: {
        section: { color: 'var(--ds-grey-400)' },
      },
    }),
    InputWrapper: InputWrapper.extend({
      styles: {
        label: {
          color: 'var(--ds-grey-500)',
          fontSize: rem(16),
          fontWeight: 500,
          marginBottom: rem(4),
        },
        description: {
          color: 'var(--ds-grey-400)',
          fontSize: rem(12),
        },
        required: {
          color: 'var(--ds-status-error)',
        },
        error: {
          color: 'var(--ds-status-error)',
          fontSize: rem(12),
          marginTop: rem(4),
        },
      },
    }),
    Textarea: Textarea.extend({
      defaultProps: {
        minRows: 5,
      },
    }),
    Checkbox: Checkbox.extend({
      defaultProps: { color: 'blue' },
      classNames: {
        input: componentClasses.checkboxInput,
        label: componentClasses.checkboxLabel,
      },
    }),
    Combobox: Combobox.extend({
      classNames: { option: componentClasses.comboboxOption },
      styles: {
        dropdown: {
          borderRadius: '2px',
          backgroundColor: 'var(--ds-surface)',
          borderColor: 'var(--ds-border-line)',
          boxShadow: 'var(--ds-shadow-light)',
        },
        options: { padding: 0 },
        option: {
          fontSize: rem(14),
          borderRadius: 0,
          padding: `${rem(8)} ${rem(12)}`,
        },
        groupLabel: { color: 'var(--ds-grey-400)' },
        empty: {
          color: 'var(--ds-grey-400)',
          fontSize: rem(13),
          padding: `${rem(8)} ${rem(12)}`,
        },
      },
    }),
    Modal: Modal.extend({
      styles: {
        content: {
          backgroundColor: 'var(--ds-surface)',
          borderRadius: rem(4),
          boxShadow: 'var(--ds-shadow-dark)',
        },
        header: {
          borderBottomColor: 'var(--ds-border-line)',
          padding: `${rem(16)} ${rem(20)}`,
        },
        title: {
          color: 'var(--ds-font-color)',
          fontSize: rem(16),
          fontWeight: 600,
        },
        close: {
          color: 'var(--ds-grey-400)',
          '&:hover': {
            backgroundColor: 'var(--ds-grey-100)',
          },
        },
        body: { padding: rem(20) },
      },
    }),
    Popover: Popover.extend({
      styles: {
        dropdown: {
          backgroundColor: 'var(--ds-surface)',
          borderColor: 'var(--ds-border-line)',
          borderRadius: rem(4),
          boxShadow: 'var(--ds-shadow-dark)',
          padding: rem(20),
        },
      },
    }),
    NativeSelect: NativeSelect.extend({
      styles: {
        input: {
          backgroundColor: 'var(--ds-surface)',
          color: 'var(--ds-font-color)',
          borderColor: 'var(--ds-border-line)',
          borderRadius: '2px',
          fontSize: rem(14),
          minHeight: rem(36),
        },
      },
    }),
    Pill: Pill.extend({
      styles: {
        root: {
          backgroundColor: 'var(--ds-platform-l10)',
          color: 'var(--ds-accent-blue-500)',
          borderRadius: '2px',
          fontSize: rem(12),
        },
        remove: {
          color: 'var(--ds-accent-blue-500)',
          '&:hover': { color: 'var(--ds-accent-blue-700)' },
        },
      },
    }),
    Switch: Switch.extend({
      defaultProps: { color: 'blue' },
      styles: {
        track: {
          backgroundColor: 'var(--ds-grey-200)',
          borderColor: 'var(--ds-border-line)',
          minWidth: rem(38),
          height: rem(20),
          borderRadius: rem(10),
          cursor: 'pointer',
        },
        thumb: {
          border: 'none',
          width: rem(16),
          height: rem(16),
          borderRadius: '50%',
        },
        label: { color: 'var(--ds-font-color)', fontSize: rem(16) },
      },
    }),
    NumberInput: NumberInput.extend({
      styles: {
        control: {
          borderColor: 'var(--ds-border-line)',
          color: 'var(--ds-grey-500)',
          '&:hover': { backgroundColor: 'var(--ds-grey-100)' },
        },
      },
    }),
    Indicator: Indicator.extend({
      styles: {
        indicator: {
          backgroundColor: 'var(--ds-accent-orange-500)',
          fontSize: rem(10),
          minWidth: rem(18),
          height: rem(18),
          borderRadius: rem(9),
        },
      },
    }),
    HoverCard: HoverCard.extend({
      styles: {
        dropdown: {
          backgroundColor: 'var(--ds-surface)',
          borderColor: 'var(--ds-border-line)',
        },
      },
    }),
    Title: Title.extend({
      styles: {
        root: { color: 'var(--ds-accent-blue-500)', fontSize: rem(24), fontWeight: 400 },
      },
    }),
    Tabs: Tabs.extend({
      classNames: tabsClasses,
    }),
    ActionIcon: ActionIcon.extend({
      classNames: actionIconClasses,
      vars: (_theme, props) => ({
        root: {
          '--ai-color': props.color ?? 'var(--ds-grey-500)',
        },
      }),
    }),
    TypographyStylesProvider: TypographyStylesProvider.extend({
      classNames: {
        root: typographyClasses.root,
      },
    }),
  },
});
